import userService from '@/services/currentUserService';
import { SUPPORTED_LOCALES } from '@/plugins/vue-i18n';
import { debounce } from 'lodash';
import {
  setUserId,
  preservePlanningFiltersOnLogout,
  restorePlanningFiltersAfterLogout,
} from '@/modules/planning/utils/planningFilterStorage';

// disable no-shadow error for stores
/* eslint-disable no-shadow, no-param-reassign */

const CACHE_EXPIRY = 1000 * 60 * 15; // 15 minutes

function isCacheValid(key) {
  const timestamp = localStorage.getItem(`${key}_timestamp`);
  if (!timestamp) return false;
  return Date.now() - parseInt(timestamp) < CACHE_EXPIRY;
}

function setCache(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
    localStorage.setItem(`${key}_timestamp`, Date.now().toString());
  } catch (e) {
    console.warn(`Failed to set cache for ${key}:`, e);
  }
}

function resetCache(key) {
  localStorage.removeItem(key);
  localStorage.removeItem(`${key}_timestamp`);
}

const initState = {
  currentDashboardPage: null,
  currentOperatingCompany: null,
  currentUser: null,
  localeDialog: false,
  customFields: null,
  createdPhaseCount: 0,
  approvalRequestCount: 0,
  createdPhaseDetails: {},
  approvalRequestDetails: {},
};

const getters = {
  currentFullscreen: state => {
    return state.currentUser?.fullscreen || false;
  },
  currentTheme: state => {
    return state.currentUser?.theme || 'light';
  },
  currentUserLocale: state => {
    return state.currentUser?.defaultLocale;
  },
  availableLocales: state => {
    return state.currentOperatingCompany?.availableLocales || SUPPORTED_LOCALES;
  },
  dashboardPages: state => {
    return state.currentUser?.dashboardPages?.filter(
      x => x.operatingCompany?.id === state.currentOperatingCompany?.id
    );
  },
  currentOperatingCompanyUrl: (state, getters) => {
    return (
      state.currentOperatingCompany?.id +
      '-' +
      getters.currentOperatingCompany?.urlFriendlyName || ''
    );
  },
  currentDashboardPageUrl: (state) => {
    return (
      state.currentDashboardPage?.id +
      '-' +
      state.currentDashboardPage?.urlFriendlyName || ''
    );
  },
  defaultDashboardPage: (state) => {
    return state.currentUser?.dashboardPages?.find(
      x => x.id == state.currentUser?.defaultDashboardPageId
    ) || state.currentUser?.dashboardPages?.[0];
  },
  currentDashboardPage: state => state.currentDashboardPage,
  operatingCompanies: state => state.currentUser?.operatingCompanies,
  // TO_DEPRECATE: below are only used so they show up in getters/mapGetters.
  // use methods from 'vuex-pathify' instead.
  currentUser: state => {
    return state.currentUser;
  },
  currentOperatingCompany: state => {
    return state.currentOperatingCompany;
  },
  currentOperatingCompanyId: state => {
    return state.currentOperatingCompany?.id;
  },
  supportedLocales: state => state.currentUser?.supportedLocales,
  customFields: state => {
    return state.customFields;
  },
  createdPhaseCount: state => {
    return state.createdPhaseCount;
  },
  createdPhaseDetails: state => {
    return state.createdPhaseDetails;
  },
  approvalRequestCount: state => {
    return state.approvalRequestCount;
  },
  approvalRequestDetails: state => {
    return state.approvalRequestDetails;
  },
};

const mutations = {
  clearStateData(state) {
    state.currentDashboardPage = null;
    state.currentOperatingCompany = null;
    state.currentUser = null;
    state.customFields = null;
    state.createdPhaseCount = 0;
    state.createdPhaseDetails = {};
    state.approvalRequestCount = 0;
    state.approvalRequestDetails = {};
  },
  currentUser(state, user) {
    state.currentUser = user;
    this.$app?.$locale(user.defaultLocale); // https://stackoverflow.com/questions/60491988/how-to-access-vue-instance-in-vuex
  },
  currentOperatingCompany(state, oc) {
    state.currentOperatingCompany = oc;
  },
  currentDashboardPage(state, v) {
    state.currentDashboardPage = v;
  },
  localeDialog(state, v) {
    state.localeDialog = v;
  },
  customFields(state, v) {
    state.customFields = v;
  },
  createdPhaseCount(state, v) {
    state.createdPhaseCount = v;
  },
  createdPhaseDetails(state, v) {
    state.createdPhaseDetails = v;
  },
  approvalRequestCount(state, v) {
    state.approvalRequestCount = v;
  },
  approvalRequestDetails(state, v) {
    state.approvalRequestDetails = v;
  },
};

const actions = {
  async init({ commit, dispatch, getters }) {
    commit('clearStateData');

    // Define promises for user and custom fields
    const userPromise = (async () => {
      if (!isCacheValid('currentUser')) {
        await dispatch('getUser');
      } else {
        const cachedUser = JSON.parse(localStorage.getItem('currentUser'));
        commit('currentUser', cachedUser);
      }
    })();

    const customFieldsPromise = (async () => {
      if (!isCacheValid('customFields')) {
        await dispatch('getCustomFields');
      } else {
        const cachedFields = JSON.parse(localStorage.getItem('customFields'));
        commit('customFields', cachedFields);
      }
    })();

    // Execute promises in parallel
    await Promise.all([userPromise, customFieldsPromise]);

    // Set Default dashboard page
    if (!getters.currentDashboardPage) {
      dispatch('setCurrentDashboardPage', getters.defaultDashboardPage);
    }
    // Set operating company based on current (default) dashboard page
    if (!getters.currentOperatingCompany) {
      dispatch(
        'setCurrentOperatingCompany',
        getters.operatingCompanies.find(
          x => x.id == getters.currentDashboardPage?.operatingCompany?.id
        ) || getters.operatingCompanies[0]
      );
    }
    dispatch('getTaskDetails');
    // Set userId for planning filters
    if (getters.currentUser?.id) {
      setUserId(getters.currentUser.id);
    }
  },
  clearStateData({ commit }) {
    commit('clearStateData');
  },
  setCurrentOperatingCompanyById({ dispatch, getters }, operatingCompanyId) {
    const oc = getters.operatingCompanies?.find(
      x => x.id === operatingCompanyId
    );
    dispatch('setCurrentOperatingCompany', oc);
  },
  async getTaskDetails({ commit, dispatch }) {
    commit('createdPhaseCount', 0);
    commit('createdPhaseDetails', {});
    commit('approvalRequestCount', 0);
    commit('approvalRequestDetails', {});
    dispatch('debouncedGetCreatedPhases');
    dispatch('debouncedGetApprovalRequests');
  },
  setCurrentOperatingCompany({ commit, dispatch, getters }, operatingCompany) {
    commit('currentOperatingCompany', operatingCompany);
    // Reset the dashboard pages if the current dashboard page doesn't exist for the operating company
    if (getters.dashboardPages.find(x => x.id == getters.currentDashboardPage?.id)) return;
    dispatch('setCurrentDashboardPage', getters.dashboardPages[0]);
  },
  async getUser({ commit }) {
    try {
      const { data } = await userService.getCurrentUser();
      resetCache('currentUser');
      commit('currentUser', data);
      setCache('currentUser', data);
      // HACK: If no locale is set, trigger localeDialog state
      // can't emit down to component
      if (!data.defaultLocale) commit('localeDialog', true);
      return data;
      // REFACTOR: use watcher in localeDialog component on currentUser.defaultLocale
    } catch (err) {
      err.errors ? console.log([...err.errors]) : console.log(err);
      throw err;
    }
  },
  async patchUser({ dispatch }, payload) {
    try {
      // REFACTOR: find a way to commit the response, instead of calling getUser again
      await userService.patchCurrentUser(payload);
      resetCache('currentUser');
      dispatch('getUser');
    } catch (err) {
      err.errors ? console.log([...err.errors]) : console.log(err);
      throw err;
    }
  },
  async logoutUser({ dispatch }) {
    try {
      // Save planning filters and planning information before logout
      const savedData = preservePlanningFiltersOnLogout();

      await userService.logout();
      dispatch('clearStateData');
      localStorage.clear();

      // Restore planning filters and planning information
      restorePlanningFiltersAfterLogout(savedData);
    } catch (err) {
      err.errors ? console.log([...err.errors]) : console.log(err);
      throw err;
    }
  },
  setCurrentDashboardPageById({ dispatch, getters }, dashboardPageId) {
    let dbp = getters.dashboardPages?.find(x => x.id === dashboardPageId);
    if (!dbp) dbp = getters.dashboardPages[0];
    dispatch('setCurrentDashboardPage', dbp);
  },
  setCurrentDashboardPage({ commit }, dashboardPage) {
    commit('currentDashboardPage', dashboardPage);
  },
  setLocale({ dispatch, state }, locale) {
    if (state.currentUser) {
      // currentUser is cleared on logout
      dispatch('patchUser', { defaultLocale: locale });
    }
  },
  setFullscreen({ dispatch, state }, value) {
    if (state.currentUser) {
      // currentUser is cleared on logout
      dispatch('patchUser', { fullscreen: value });
    }
  },
  setTheme({ dispatch, state }, value) {
    if (state.currentUser) {
      // currentUser is cleared on logout
      dispatch('patchUser', { theme: value });
    }
  },
  async getCustomFields({ commit }) {
    try {
      const { data } = await userService.getCustomFields();
      commit('customFields', data);
      setCache('customFields', data);
    } catch (e) {
      console.log(e);
    }
  },
  debouncedGetCreatedPhases({ dispatch }) {
    debounce(function() {
      dispatch('getCreatedPhases');
    }, 750)();
  },
  async getCreatedPhases({ commit }) {
    try {
      const { data } = await userService.getCreatedPhaseDetails();
      commit('createdPhaseCount', data?.totalTaskCount || 0);
      commit('createdPhaseDetails', data);
    } catch (e) {
      console.log(e);
    }
  },
  debouncedGetApprovalRequests({ dispatch }) {
    debounce(function() {
      dispatch('getApprovalRequests');
    }, 750)();
  },
  async getApprovalRequests({ commit }) {
    try {
      const { data } = await userService.getApprovalRequestDetails();
      commit('approvalRequestCount', data?.totalApprovalRequestCount || 0);
      commit('approvalRequestDetails', data);
    } catch (e) {
      console.log(e);
    }
  },
};

export default {
  namespaced: true,
  state: () => initState,
  getters,
  mutations,
  actions,
};
